import { useQuery } from '@tanstack/react-query'
import { lazy, Suspense, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { AttentionList, AttentionRow } from '@/components/console/attention'
import { Button } from '@/components/console/button'
import { useFrameIdentity } from '@/components/console/frame/use-frame-identity'
import { Modal, ModalClose } from '@/components/console/modal'
import { Icon } from '@/components/icons/icon'
import { useTheme } from '@/components/theme/theme-provider'
import { healthQuery } from '@/lib/api/queries'
import { useDemo } from '@/lib/demo/demo-context'
import { FRAME_SAMPLE } from '@/lib/demo/frame'
import { useConsoleRole } from '@/lib/demo/use-console-role'
import { useOnline } from '@/lib/demo/use-data-state'
import { cn } from '@/lib/utils'

// Dev only. In a production build the condition is the literal `false`, so the
// import is dropped and the controls never ship.
const DemoControls = import.meta.env.DEV ? lazy(() => import('@/lib/demo/demo-controls')) : null

interface ConsoleHeaderProps {
  title: string
  navOpen: boolean
  onOpenNav: () => void
}

/** The header (`header.top`): page title, search, status, controls, Create agent. */
export function ConsoleHeader({ title, navOpen, onOpenNav }: ConsoleHeaderProps) {
  const { workspace } = useFrameIdentity()
  const role = useConsoleRole()
  const canCreateAgents = role.canAct && (role.role === 'owner' || role.role === 'admin')

  return (
    <header className="top">
      <button
        type="button"
        className="iconbtn burger"
        aria-label="Open navigation"
        aria-expanded={navOpen}
        aria-controls="side"
        onClick={onOpenNav}
      >
        <Icon name="menu" />
      </button>
      <div className="crumb">
        {workspace ? <span className="wsname">{workspace.name} /</span> : null}
        <h1 id="pageTitle">{title}</h1>
      </div>
      <div className="sp" />
      <GlobalSearch />
      <StatusChip />
      {DemoControls ? (
        <Suspense fallback={null}>
          <DemoControls />
        </Suspense>
      ) : null}
      <ThemeButton />
      <Notifications />
      {canCreateAgents ? (
        // Named outright: on a phone the label is hidden and only the "+" shows.
        <Link className="btn btn--primary hdr-create" to="/dashboard/agents/new" aria-label="Create agent">
          <Icon name="plus" />
          <span className="lbl">Create agent</span>
        </Link>
      ) : null}
    </header>
  )
}

/** "/" focuses it from anywhere; Enter searches the call log. Hidden on mobile, as designed. */
function GlobalSearch() {
  const navigate = useNavigate()
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      // Only when it is on screen: the mobile header hides it.
      if (!input.current?.offsetParent) return
      event.preventDefault()
      input.current.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const q = event.currentTarget.value.trim()
    if (event.key === 'Enter' && q) void navigate(`/dashboard/calls?q=${encodeURIComponent(q)}`)
    if (event.key === 'Escape') event.currentTarget.blur()
  }

  return (
    <div className="search">
      <Icon name="search" />
      <input
        ref={input}
        type="search"
        id="globalSearch"
        placeholder="Search calls, callers, bookings…"
        aria-label="Search calls, callers and bookings"
        onKeyDown={onKeyDown}
      />
      <span className="kbd" aria-hidden="true">
        /
      </span>
    </div>
  )
}

const timeFormat = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' })

/**
 * The service status chip. Truthful by construction: it reports only what the
 * console can see -- whether the RingOut API answered its last health check --
 * and never a hard-coded "all systems operational" (design docs §01).
 */
function StatusChip() {
  const demo = useDemo()
  const online = useOnline()
  const health = useQuery(healthQuery)
  const [open, setOpen] = useState(false)

  const sample = import.meta.env.DEV && demo.enabled ? FRAME_SAMPLE : null
  const checkedAt = health.isError ? health.errorUpdatedAt : health.dataUpdatedAt
  const checked = checkedAt ? timeFormat.format(checkedAt) : null

  let text: string
  let offline = false
  if (sample) {
    text = sample.status(demo.dataState)
    offline = demo.dataState === 'offline'
  } else if (!online) {
    text = 'Offline · cached data'
    offline = true
  } else if (health.isPending) {
    text = 'Checking service…'
  } else if (health.isError) {
    text = 'Can’t reach RingOut'
    offline = true
  } else if (health.data.draining) {
    text = 'Service restarting'
  } else {
    text = 'Service running'
  }

  return (
    <>
      <button
        type="button"
        className={cn('statuschip', offline && 'offline')}
        title="Service status"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <span className="dot" aria-hidden="true" />
        <span>{text}</span>
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Service status"
        actions={<ModalClose className="btn btn--neutral">Close</ModalClose>}
      >
        {sample ? (
          <>
            <p>
              This prototype runs on mock data, so the chip reads <b>“Demo data · no incidents”</b> rather than
              claiming live status.
            </p>
            <p style={{ marginTop: 8 }}>
              In product, call answering, calendar sync and recordings each report independently, and degraded
              states say what still works. Never a hard-coded “all systems operational”.
            </p>
          </>
        ) : (
          <>
            <p>
              {!online
                ? 'This browser is offline. The console is showing what it last loaded.'
                : health.isPending
                  ? 'The console is checking whether it can reach RingOut.'
                  : health.isError
                    ? `The console couldn’t reach RingOut at its last check${checked ? ` (${checked})` : ''}. It tries again every 30 seconds.`
                    : health.data.draining
                      ? `RingOut is restarting for an update${checked ? ` (checked ${checked})` : ''}. The console checks again every 30 seconds.`
                      : `The console reached RingOut at ${checked ?? 'its last check'}, in region ${health.data.region.toUpperCase()}. It checks every 30 seconds.`}
            </p>
            <p style={{ marginTop: 8 }}>
              Call answering, calendar sync and recordings will each report their own status here once they’re
              connected. Until then, this shows only whether the console can reach RingOut.
            </p>
          </>
        )}
      </Modal>
    </>
  )
}

function ThemeButton() {
  const { resolvedTheme, setTheme } = useTheme()
  const dark = resolvedTheme === 'dark'
  return (
    <button
      type="button"
      className="iconbtn"
      aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
      title="Switch theme"
      onClick={() => setTheme(dark ? 'light' : 'dark')}
    >
      <Icon name={dark ? 'sun' : 'moon'} />
    </button>
  )
}

/** The bell. There is no notification source yet, so live it opens an honest empty list. */
function Notifications() {
  const demo = useDemo()
  const [open, setOpen] = useState(false)
  const [read, setRead] = useState(false)

  const sample = import.meta.env.DEV && demo.enabled && demo.dataState !== 'empty' ? FRAME_SAMPLE : null
  const unread = sample && !read ? sample.notifications.length : 0

  return (
    <>
      <button
        type="button"
        className="iconbtn"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <Icon name="bell" />
        {unread ? <span className="bub">{unread}</span> : null}
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Notifications"
        actions={
          sample ? (
            <>
              <Button
                variant="quiet"
                onClick={() => {
                  setRead(true)
                  setOpen(false)
                  toast('All notifications marked read.')
                }}
              >
                Mark all read
              </Button>
              <ModalClose className="btn btn--neutral">Close</ModalClose>
            </>
          ) : (
            <ModalClose className="btn btn--neutral">Close</ModalClose>
          )
        }
      >
        {sample ? (
          <AttentionList className="mt-[6px]">
            {sample.notifications.map((n) => (
              <AttentionRow
                key={n.title}
                icon={n.icon}
                tone={n.tone}
                title={n.title}
                body={n.body}
                actions={
                  <Link className="btn btn--xs btn--outline" to={n.to} onClick={() => setOpen(false)}>
                    Open
                  </Link>
                }
              />
            ))}
          </AttentionList>
        ) : (
          <p>No notifications yet.</p>
        )}
      </Modal>
    </>
  )
}
